/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Download, Share2, Eye, EyeOff, Edit3, CheckCircle2, AlertTriangle,
  Plus, Trash2, GripVertical,
  MapPin, Phone, Clock, Users, Film, Camera, Mic, Lightbulb,
  Truck, Palette, Sparkles, Shield, FileText, Image, PanelLeftClose,
  PanelLeft, X, ArrowLeft, RefreshCw, Lock, Unlock, Search, Check,
  Settings, Copy, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { PdfLanguageDialog } from '../components/callsheet/PdfLanguageDialog';
import { downloadCallSheetPdf, fetchCallSheetPdfBlob, downloadBlobFile } from '../utils/callsheetPdf';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Department colors ──────────────────────────────────────────────────────
const DEPT_COLORS = {
  'Production': '#6366F1', 'Direction': '#E8C547', 'Camera': '#3B82F6',
  'Sound': '#10B981', 'Lighting': '#F59E0B', 'Grip': '#8B5CF6',
  'Art Department': '#EC4899', 'Costume': '#14B8A6', 'Makeup & Hair': '#F97316',
  'Transport': '#64748B', 'VFX': '#A78BFA', 'Stunts': '#EF4444',
};

const DEPT_ICONS = {
  'Production': Film, 'Direction': Camera, 'Camera': Camera, 'Sound': Mic,
  'Lighting': Lightbulb, 'Grip': Truck, 'Art Department': Palette,
  'Costume': Sparkles, 'Makeup & Hair': Sparkles, 'Transport': Truck,
  'VFX': Sparkles, 'Stunts': Shield,
};

const TIMELINE_MILESTONES = [
  { key: 'crew_call', label: 'Crew Call', color: '#6366F1' },
  { key: 'breakfast', label: 'Breakfast', color: '#10B981' },
  { key: 'lunch',     label: 'Lunch',     color: '#F59E0B' },
  { key: 'dinner',    label: 'Dinner',    color: '#EC4899' },
];

const DEPT_NOTES_LIST = [
  { key: 'ad_team',    label: 'AD Team',    color: '#E8C547' },
  { key: 'production', label: 'Production', color: '#6366F1' },
  { key: 'direction',  label: 'Direction',  color: '#E8C547' },
  { key: 'art',        label: 'Art Dept',   color: '#EC4899' },
  { key: 'costume',    label: 'Costume',    color: '#14B8A6' },
  { key: 'makeup',     label: 'Makeup',     color: '#F97316' },
  { key: 'camera',     label: 'Camera',     color: '#3B82F6' },
  { key: 'sound',      label: 'Sound',      color: '#10B981' },
  { key: 'lighting',   label: 'Lighting',   color: '#F59E0B' },
  { key: 'vfx',        label: 'VFX',        color: '#A78BFA' },
  { key: 'transport',  label: 'Transport',  color: '#64748B' },
  { key: 'stunts',     label: 'Stunts',     color: '#EF4444' },
];

const DEFAULT_LAYOUT = [
  { id: 'header',      label: 'Overview',            visible: true,  locked: true,  order: 0, icon: Film          },
  { id: 'summary',     label: 'Production Summary',  visible: true,  locked: true,  order: 1, icon: CheckCircle2  },
  { id: 'emergency',   label: 'Emergency',           visible: true,  locked: false, order: 2, icon: AlertTriangle  },
  { id: 'timeline',    label: 'Timeline',            visible: true,  locked: false, order: 3, icon: Clock          },
  { id: 'scenes',      label: 'Scenes',              visible: true,  locked: false, order: 4, icon: FileText       },
  { id: 'cast',        label: 'Cast',                visible: true,  locked: false, order: 5, icon: Users          },
  { id: 'crew',        label: 'Crew',                visible: true,  locked: false, order: 6, icon: Camera         },
  { id: 'locations',   label: 'Locations',           visible: true,  locked: false, order: 7, icon: MapPin         },
  { id: 'dept-notes',  label: 'Dept Notes',          visible: true,  locked: false, order: 8, icon: Edit3          },
  { id: 'attachments', label: 'Attachments',         visible: false, locked: false, order: 9, icon: Image          },
];

const getCallSheetPdfFilename = (sheet) => `callsheet_day${sheet?.shooting_day || 1}_${sheet?.date || 'draft'}.pdf`;
const SUMMARY_FIELDS = [
  { key: 'shooting_day', label: 'Shooting Day', icon: Film, color: 'var(--cb-accent)' },
  { key: 'total_scenes', label: 'Total Scenes', icon: FileText, color: '#6366F1' },
  { key: 'script_pages', label: 'Script Pages', icon: FileText, color: '#3B82F6' },
  { key: 'cast_working', label: 'Cast Working', icon: Users, color: '#EC4899' },
  { key: 'crew_count', label: 'Crew Count', icon: Camera, color: '#10B981' },
  { key: 'locations_count', label: 'Locations Count', icon: MapPin, color: '#F59E0B' },
  { key: 'crew_call', label: 'Crew Call', icon: Clock, color: '#8B5CF6' },
  { key: 'unit', label: 'Unit', icon: Film, color: '#14B8A6' },
];
const KEY_PRODUCTION_CONTACTS = [
  { label: 'Director', field: 'director' },
  { label: 'DOP / Cinematographer', field: 'dop' },
  { label: '1st Assistant Director', field: 'first_ad' },
  { label: 'Producer', field: 'producer' },
  { label: 'Executive Producer', field: 'executive_producer' },
  { label: 'Line Producer', field: 'line_producer' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
function useDebouncedCallback(fn, delay = 1000) {
  const timer = useRef();
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

function SectionHeader({ id, icon: Icon, title, accent, action }) {
  return (
    <div id={id} className="flex items-center justify-between mb-4 pb-3"
         style={{ borderBottom: '1px solid var(--cb-border)' }}>
      <div className="flex items-center gap-2.5">
        <div className="w-1 h-6 rounded-full" style={{ background: accent || 'var(--cb-accent)' }} />
        {Icon && <Icon className="h-4 w-4" style={{ color: accent || 'var(--cb-accent)' }} />}
        <h2 className="text-base font-bold tracking-tight" style={{ color: 'var(--cb-text)' }}>{title}</h2>
      </div>
      {action}
    </div>
  );
}

function getSceneGroupKey(scene) {
  return (scene.real_location || scene.location || '').trim().toUpperCase() || 'UNASSIGNED LOCATION';
}

function buildLocationSceneGroups(scenes = []) {
  const orderedScenes = [...scenes].sort((a, b) => (a.order || 0) - (b.order || 0));
  const groups = [];
  const groupMap = new Map();

  orderedScenes.forEach((scene) => {
    const key = getSceneGroupKey(scene);
    if (!groupMap.has(key)) {
      const nextGroup = { key, name: key, scenes: [], totalPages: 0 };
      groupMap.set(key, nextGroup);
      groups.push(nextGroup);
    }
    const current = groupMap.get(key);
    current.scenes.push(scene);
    current.totalPages += Number(scene.pages || 0);
  });

  return groups.map((group) => ({
    ...group,
    totalPages: Number(group.totalPages || 0),
    sceneCount: group.scenes.length,
  }));
}

function EField({ value, onChange, placeholder, mono = false, multiline = false, small = false }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value || '');
  useEffect(() => setLocal(value || ''), [value]);

  const cls = `w-full outline-none bg-transparent rounded px-1 ${small ? 'text-xs' : 'text-sm'} ${mono ? 'font-mono' : ''}`;
  const style = {
    color: 'var(--cb-text)',
    borderBottom: editing ? '1px solid var(--cb-accent)' : '1px solid transparent',
  };

  if (multiline) {
    return (
      <textarea className={cls + ' resize-none min-h-[60px]'} style={style}
        value={local} placeholder={placeholder}
        onChange={e => setLocal(e.target.value)}
        onFocus={() => setEditing(true)}
        onBlur={() => { setEditing(false); if (local !== value) onChange(local); }} />
    );
  }
  return (
    <input className={cls} style={style} value={local} placeholder={placeholder}
      onChange={e => setLocal(e.target.value)}
      onFocus={() => setEditing(true)}
      onBlur={() => { setEditing(false); if (local !== value) onChange(local); }} />
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function CallSheetPage() {
  const { projectId, sheetId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [sheet, setSheet]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [navOpen, setNavOpen]     = useState(true);
  const [scheduleDays, setScheduleDays] = useState([]);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncing, setSyncing]     = useState(false);
  const [showScenePicker, setShowScenePicker] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [customizeMode, setCustomizeMode] = useState(false);
  const [sectionLayout, setSectionLayout] = useState(DEFAULT_LAYOUT);
  const [activeSection, setActiveSection] = useState('header');
  const [savingDefault, setSavingDefault] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showPdfLanguageDialog, setShowPdfLanguageDialog] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    loadSheet();
    loadScheduleDays();
    // eslint-disable-next-line
  }, [sheetId]);

  async function loadSheet() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets/${sheetId}`, { headers });
      if (!r.ok) { navigate(`/projects/${projectId}/callsheet`); return; }
      const data = await r.json();
      setSheet(data);
      // Load section layout from sheet or default
      if (data.section_layout && data.section_layout.length > 0) {
        // Merge icons AND locked property from DEFAULT_LAYOUT into the stored layout
        const iconMap   = Object.fromEntries(DEFAULT_LAYOUT.map(s => [s.id, s.icon]));
        const lockedMap = Object.fromEntries(DEFAULT_LAYOUT.map(s => [s.id, s.locked || false]));
        const merged = data.section_layout.map(s => ({
          ...s,
          icon:   iconMap[s.id]   || FileText,
          locked: lockedMap[s.id] || false,
        }));
        setSectionLayout(merged);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadScheduleDays() {
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets-schedule-days`, { headers });
      if (r.ok) setScheduleDays(await r.json());
    } catch (error) { console.error('Failed to load call sheet schedule days', error); }
  }

  // ── Debounced auto-save ──────────────────────────────────────────────────
  const debouncedSave = useDebouncedCallback(async (updates) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets/${sheetId}`, {
        method: 'PATCH', headers, body: JSON.stringify(updates)
      });
      if (r.ok) setSheet(await r.json());
    } finally {
      setSaving(false);
    }
  }, 900);

  function update(field, value) {
    setSheet(s => ({ ...s, [field]: value }));
    debouncedSave({ [field]: value });
  }

  function updateNested(field, key, value) {
    const newObj = { ...(sheet[field] || {}), [key]: value };
    setSheet(s => ({ ...s, [field]: newObj }));
    debouncedSave({ [field]: newObj });
  }

  // ── Section layout management ────────────────────────────────────────────
  function onNavDragEnd(result) {
    if (!result.destination) return;
    const locked = sectionLayout.filter(s => s.locked).sort((a, b) => a.order - b.order);
    const movable = sectionLayout.filter(s => !s.locked).sort((a, b) => a.order - b.order);
    const [moved] = movable.splice(result.source.index, 1);
    movable.splice(result.destination.index, 0, moved);
    // Reorder movable starting after locked ones
    const lockedCount = locked.length;
    const reordered = [
      ...locked,
      ...movable.map((s, i) => ({ ...s, order: lockedCount + i })),
    ];
    setSectionLayout(reordered);
    debouncedSave({ section_layout: reordered.map(({ icon, ...rest }) => rest) });
  }

  function toggleSectionVisibility(id) {
    const sec = sectionLayout.find(s => s.id === id);
    if (sec?.locked) return; // can't hide locked sections
    const updated = sectionLayout.map(s => s.id === id ? { ...s, visible: !s.visible } : s);
    setSectionLayout(updated);
    debouncedSave({ section_layout: updated.map(({ icon, ...rest }) => rest) });
  }

  async function saveAsDefault() {
    setSavingDefault(true);
    try {
      const layout = sectionLayout.map(({ icon, ...rest }) => rest);
      await fetch(`${API}/api/projects/${projectId}/call-sheet-layout`, {
        method: 'PATCH', headers, body: JSON.stringify({ layout })
      });
      toast.success('Layout saved as default for all future call sheets');
    } catch { toast.error('Failed to save default'); }
    finally { setSavingDefault(false); }
  }

  // ── PDF Download ─────────────────────────────────────────────────────────
  async function downloadPdf(synopsisLang = 'english') {
    setDownloadingPdf(true);
    try {
      await downloadCallSheetPdf({
        apiBase: API,
        projectId,
        sheetId,
        token,
        synopsisLang,
        filename: getCallSheetPdfFilename(sheet),
      });
      toast.success('PDF downloaded!');
    } catch (error) { toast.error(error.message || 'PDF download failed'); }
    finally { setDownloadingPdf(false); }
  }

  // ── Publish ──────────────────────────────────────────────────────────────
  async function togglePublish() {
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets/${sheetId}/publish`, {
        method: 'POST', headers
      });
      if (r.ok) {
        const updated = await r.json();
        setSheet(updated);
        toast.success(updated.status === 'published' ? 'Published!' : 'Reverted to draft');
      }
    } catch { toast.error('Failed'); }
  }

  // ── Sync ─────────────────────────────────────────────────────────────────
  async function handleSync(dayId) {
    setSyncing(true);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/call-sheets/${sheetId}/sync?day_id=${dayId}`, {
        method: 'POST', headers
      });
      if (!r.ok) throw new Error();
      setSheet(await r.json());
      setShowSyncModal(false);
      toast.success('Synced from schedule!');
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  }

  // ── Scene picker handler ─────────────────────────────────────────────────
  function handleScenesAdded(newScenes) {
    const existing = sheet.scenes || [];
    const existingIds = new Set(existing.map(s => s.scene_id));
    const toAdd = newScenes.filter(s => !existingIds.has(s.scene_id));
    const allScenes = [...existing, ...toAdd].map((s, i) => ({ ...s, order: i }));
    update('scenes', allScenes);
    // Auto-add cast from new scenes
    const existingCast = new Set((sheet.cast_call_times || []).map(c => c.character));
    const newCast = [];
    toAdd.forEach(sc => {
      (sc.cast || []).forEach(char => {
        if (char && !existingCast.has(char)) {
          existingCast.add(char);
          newCast.push({
            id: crypto.randomUUID(), character: char, actor_name: '',
            pickup_call: '', on_set_call: sheet.crew_call || '',
            contact: '', status: 'Pending',
          });
        }
      });
    });
    if (newCast.length > 0) {
      const allCast = [...(sheet.cast_call_times || []), ...newCast];
      setSheet(s => ({ ...s, cast_call_times: allCast }));
      debouncedSave({ cast_call_times: allCast });
      toast.success(`Added ${toAdd.length} scene(s), ${newCast.length} cast member(s) auto-added`);
    }
    setShowScenePicker(false);
  }

  // ── Scene DnD ────────────────────────────────────────────────────────────
  function onSceneDragEnd(result) {
    if (!result.destination) return;
    const scenes = Array.from(sheet.scenes || []);
    const [moved] = scenes.splice(result.source.index, 1);
    scenes.splice(result.destination.index, 0, moved);
    update('scenes', scenes.map((s, i) => ({ ...s, order: i })));
  }

  // ── Intersection observer for nav ────────────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) setActiveSection(visible[0].target.id.replace('-anchor', ''));
      },
      { threshold: 0.2, rootMargin: '-60px 0px 0px 0px' }
    );
    sectionLayout.forEach(s => {
      const el = document.getElementById(`${s.id}-anchor`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading, sectionLayout]);

  function scrollTo(id) {
    const el = document.getElementById(`${id}-anchor`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--cb-bg)' }}>
      <div style={{ color: 'var(--cb-text-dim)' }}>Loading call sheet…</div>
    </div>
  );

  if (!sheet) return null;

  const sheetWithDefaults = {
    production_summary: {},
    production_team: [],
    scenes: [],
    ...sheet,
  };

  const isPublished   = sheetWithDefaults.status === 'published';
  const totalPages    = (sheetWithDefaults.scenes || []).reduce((a, s) => a + (s.pages || 0), 0);
  const sortedLayout  = [...sectionLayout].sort((a, b) => a.order - b.order);
  const visibleLayout = sortedLayout.filter(s => s.visible);

  // Section component map
  const SECTION_MAP = {
    header:       <HeaderSection sheet={sheetWithDefaults} update={update} updateNested={updateNested} isPublished={isPublished} previewMode={previewMode} />,
    emergency:    <EmergencySection sheet={sheetWithDefaults} update={update} previewMode={previewMode} />,
    summary:      <SummarySection sheet={sheetWithDefaults} totalPages={totalPages} update={update} previewMode={previewMode} />,
    timeline:     <TimelineSection sheet={sheetWithDefaults} updateNested={updateNested} previewMode={previewMode} />,
    scenes:       <ScenesSection sheet={sheetWithDefaults} update={update} onDragEnd={onSceneDragEnd} onOpenPicker={() => setShowScenePicker(true)} onOpenLocations={() => scrollTo('locations')} previewMode={previewMode} />,
    cast:         <CastSection sheet={sheetWithDefaults} update={update} previewMode={previewMode} />,
    crew:         <CrewSection sheet={sheetWithDefaults} update={update} previewMode={previewMode} />,
    locations:    <LocationsSection sheet={sheetWithDefaults} update={update} previewMode={previewMode} />,
    'dept-notes': <DeptNotesSection sheet={sheetWithDefaults} updateNested={updateNested} previewMode={previewMode} />,
    attachments:  <AttachmentsSection sheet={sheetWithDefaults} update={update} />,
  };

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--cb-bg)' }} data-testid="callsheet-page">

      {/* ── Left Navigation ──────────────────────────────────────────── */}
      <div
        className={`hidden md:flex flex-col sticky top-0 h-screen shrink-0 transition-all duration-200 ${navOpen ? 'w-52' : 'w-14'}`}
        style={{ background: 'var(--cb-surface)', borderRight: '1px solid var(--cb-border)', zIndex: 20 }}
      >
        {/* Nav header */}
        <div className="flex items-center justify-between px-3 py-3"
             style={{ borderBottom: '1px solid var(--cb-border)' }}>
          {navOpen && (
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--cb-text-dim)' }}>
              {customizeMode ? 'Customize' : 'Sections'}
            </span>
          )}
          <div className="flex items-center gap-1">
            {navOpen && (
              <button
                onClick={() => setCustomizeMode(m => !m)}
                className="p-1 rounded transition-colors"
                title="Customize layout"
                style={{ color: customizeMode ? 'var(--cb-accent)' : 'var(--cb-text-dim)', background: customizeMode ? 'var(--cb-accent-dim)' : 'transparent' }}>
                <Settings className="h-3.5 w-3.5" />
              </button>
            )}
            <button onClick={() => setNavOpen(o => !o)} className="p-1 rounded"
                    style={{ color: 'var(--cb-text-dim)' }}>
              {navOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-2">
          {customizeMode && navOpen ? (
            <>
              {/* Locked sections (pinned, not draggable) */}
              {sortedLayout.filter(s => s.locked).map(sec => {
                const Icon = sec.icon || FileText;
                return (
                  <div key={sec.id} className="flex items-center gap-2 px-2 py-1.5 opacity-60">
                    <Lock className="h-3 w-3 shrink-0" style={{ color: 'var(--cb-accent)' }} />
                    <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--cb-accent)' }} />
                    <span className="flex-1 text-xs truncate font-medium" style={{ color: 'var(--cb-text)' }}>{sec.label}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'var(--cb-accent-dim)', color: 'var(--cb-accent)' }}>fixed</span>
                  </div>
                );
              })}
              <div className="mx-2 my-1 h-px" style={{ background: 'var(--cb-border)' }} />
              {/* Draggable sections */}
              <DragDropContext onDragEnd={onNavDragEnd}>
                <Droppable droppableId="nav-sections">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {sortedLayout.filter(s => !s.locked).map((sec, index) => {
                        const Icon = sec.icon || FileText;
                        return (
                          <Draggable key={sec.id} draggableId={sec.id} index={index}>
                            {(prov, snap) => (
                              <div ref={prov.innerRef} {...prov.draggableProps}
                                   className="flex items-center gap-2 px-2 py-1.5 group rounded mx-1"
                                   style={{
                                     background: snap.isDragging ? 'var(--cb-surface-h)' : 'transparent',
                                     boxShadow: snap.isDragging ? '0 4px 16px rgba(0,0,0,0.3)' : 'none',
                                   }}>
                                <div {...prov.dragHandleProps} className="cursor-grab active:cursor-grabbing opacity-40 hover:opacity-100">
                                  <GripVertical className="h-3.5 w-3.5" style={{ color: 'var(--cb-text-dim)' }} />
                                </div>
                                <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: sec.visible ? 'var(--cb-text-sec)' : 'var(--cb-text-dim)', opacity: sec.visible ? 1 : 0.4 }} />
                                <span className="flex-1 text-xs truncate" style={{ color: sec.visible ? 'var(--cb-text)' : 'var(--cb-text-dim)', opacity: sec.visible ? 1 : 0.5 }}>
                                  {sec.label}
                                </span>
                                <button onClick={() => toggleSectionVisibility(sec.id)}
                                        className="opacity-60 hover:opacity-100 p-0.5 transition-opacity">
                                  {sec.visible
                                    ? <Eye className="h-3.5 w-3.5" style={{ color: 'var(--cb-accent)' }} />
                                    : <EyeOff className="h-3.5 w-3.5" style={{ color: 'var(--cb-text-dim)' }} />
                                  }
                                </button>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              {/* Live preview strip */}
              <div className="mx-2 mt-3 mb-1">
                <div className="text-[9px] uppercase tracking-widest font-bold mb-1.5" style={{ color: 'var(--cb-text-dim)' }}>Preview Order</div>
                {sortedLayout.filter(s => s.visible).map((sec, i) => (
                  <div key={sec.id} className="flex items-center gap-1.5 py-0.5 px-1 rounded text-[10px]"
                       style={{ color: sec.locked ? 'var(--cb-accent)' : 'var(--cb-text-sec)' }}>
                    <span className="font-mono opacity-50 w-3 text-right shrink-0">{i + 1}</span>
                    <span className="truncate">{sec.label}</span>
                    {sec.locked && <Lock className="h-2.5 w-2.5 shrink-0 opacity-60" />}
                  </div>
                ))}
              </div>
            </>
          ) : (
            visibleLayout.map(sec => {
              const Icon = sec.icon || FileText;
              const active = activeSection === sec.id;
              return (
                <button key={sec.id} onClick={() => scrollTo(sec.id)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-all duration-150 ${navOpen ? '' : 'justify-center'}`}
                        style={{
                          background: active ? 'var(--cb-surface-h)' : 'transparent',
                          color: active ? 'var(--cb-accent)' : 'var(--cb-text-sec)',
                          borderLeft: active ? '2px solid var(--cb-accent)' : '2px solid transparent',
                        }}>
                  <Icon className="h-4 w-4 shrink-0" />
                  {navOpen && <span className="font-medium truncate">{sec.label}</span>}
                </button>
              );
            })
          )}
        </nav>

        {/* Customize footer */}
        {customizeMode && navOpen && (
          <div className="px-3 py-2" style={{ borderTop: '1px solid var(--cb-border)' }}>
            <button
              onClick={saveAsDefault}
              disabled={savingDefault}
              className="w-full text-xs py-1.5 px-3 rounded-lg font-medium transition-colors"
              style={{ background: 'var(--cb-accent)', color: '#000' }}>
              {savingDefault ? 'Saving…' : 'Set as Default'}
            </button>
          </div>
        )}

        {/* Saving indicator */}
        <div className="px-3 py-2" style={{ borderTop: customizeMode ? 'none' : '1px solid var(--cb-border)' }}>
          {saving ? (
            <div className={`flex items-center gap-1.5 text-xs ${navOpen ? '' : 'justify-center'}`}
                 style={{ color: 'var(--cb-text-dim)' }}>
              <div className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
              {navOpen && 'Saving…'}
            </div>
          ) : (
            <div className={`flex items-center gap-1.5 text-xs ${navOpen ? '' : 'justify-center'}`}
                 style={{ color: 'var(--cb-text-dim)' }}>
              <div className="h-1.5 w-1.5 rounded-full" style={{ background: '#22C55E' }} />
              {navOpen && 'Saved'}
            </div>
          )}
        </div>
      </div>

      {/* ── Main Content ─────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 overflow-y-auto">

        {/* Top action bar */}
        <div className="sticky top-0 z-10 px-4 py-2 flex items-center justify-between gap-3"
             style={{ background: 'var(--cb-surface)', borderBottom: '1px solid var(--cb-border)' }}>
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(`/projects/${projectId}/callsheet`)}
                    className="p-1.5 rounded-md"
                    style={{ color: 'var(--cb-text-sec)' }}>
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              <div className="text-sm font-bold truncate" style={{ color: 'var(--cb-text)' }}>{sheet.title}</div>
              <div className="text-xs font-mono" style={{ color: 'var(--cb-text-dim)' }}>
                Day {sheet.shooting_day} · {sheet.date} · {sheet.version_label || 'v1'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {scheduleDays.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setShowSyncModal(true)}
                      className="gap-1.5 text-xs hidden sm:flex"
                      style={{ borderColor: 'var(--cb-border)', color: 'var(--cb-text-sec)' }}
                      data-testid="sync-btn">
                <RefreshCw className="h-3.5 w-3.5" /> Sync
              </Button>
            )}
            <Button size="sm" variant="outline"
                    onClick={() => setPreviewMode(m => !m)}
                    className="gap-1.5 text-xs hidden sm:flex"
                    style={{
                      borderColor: previewMode ? 'var(--cb-accent)' : 'var(--cb-border)',
                      color: previewMode ? 'var(--cb-accent)' : 'var(--cb-text-sec)',
                      background: previewMode ? 'var(--cb-accent-dim)' : 'transparent',
                    }}
                    data-testid="preview-mode-btn">
              <Eye className="h-3.5 w-3.5" /> {previewMode ? 'Editing' : 'Preview'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowShare(true)}
                    className="gap-1.5 text-xs hidden sm:flex"
                    style={{ borderColor: 'var(--cb-border)', color: 'var(--cb-text-sec)' }}
                    data-testid="share-btn">
              <Share2 className="h-3.5 w-3.5" /> Share
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => setShowPdfLanguageDialog(true)}
                    disabled={downloadingPdf}
                    className="gap-1.5 text-xs hidden sm:flex"
                    style={{ borderColor: 'var(--cb-border)', color: 'var(--cb-text-sec)' }}
                    data-testid="export-pdf-btn">
              <Download className="h-3.5 w-3.5" /> {downloadingPdf ? 'Generating…' : 'PDF'}
            </Button>
            <Button size="sm" onClick={togglePublish}
                    className="gap-1.5 text-xs font-bold"
                    style={{
                      background: isPublished ? 'rgba(34,197,94,0.15)' : 'var(--cb-accent)',
                      color: isPublished ? '#22C55E' : '#000',
                      border: isPublished ? '1px solid rgba(34,197,94,0.3)' : 'none'
                    }}
                    data-testid="publish-btn">
              {isPublished ? <><Lock className="h-3.5 w-3.5" /> Published</> : <><Unlock className="h-3.5 w-3.5" /> Publish</>}
            </Button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-10">
          {previewMode && (
            <div className="flex items-center justify-between px-4 py-2 rounded-xl mb-2"
                 style={{ background: 'rgba(232,200,71,0.08)', border: '1px solid rgba(232,200,71,0.2)' }}>
              <span className="text-xs font-medium" style={{ color: 'var(--cb-accent)' }}>
                Preview mode — showing how the call sheet looks
              </span>
              <button onClick={() => setPreviewMode(false)} className="text-xs"
                      style={{ color: 'var(--cb-text-dim)' }}>Exit</button>
            </div>
          )}
          {visibleLayout.map(sec => (
            <div key={sec.id} id={`${sec.id}-anchor`}
                 style={previewMode ? { pointerEvents: 'none', userSelect: 'none', opacity: 0.95 } : {}}>
              {SECTION_MAP[sec.id]}
            </div>
          ))}
          <div className="h-24" />
        </div>
      </div>

      {/* ── Sync Modal ────────────────────────────────────────────── */}
      {showSyncModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
             onClick={() => setShowSyncModal(false)}>
          <div className="w-full max-w-md rounded-2xl p-6"
               style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--cb-text)' }}>Sync from Schedule</h3>
              <button onClick={() => setShowSyncModal(false)} style={{ color: 'var(--cb-text-sec)' }}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--cb-text-sec)' }}>
              Choose a shoot day to pull scenes, cast & locations from your schedule.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {scheduleDays.map(day => (
                <button key={day.id} onClick={() => handleSync(day.id)} disabled={syncing}
                        className="w-full text-left px-4 py-3 rounded-lg transition-colors"
                        style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text)' }}
                        data-testid={`sync-day-${day.id}`}>
                  <div className="font-bold text-sm">Day {day.day_number}: {day.day_name}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'var(--cb-text-dim)' }}>
                    {day.scene_ids?.length || 0} scenes
                  </div>
                </button>
              ))}
            </div>
            {syncing && <div className="text-center mt-4 text-sm" style={{ color: 'var(--cb-accent)' }}>Syncing…</div>}
          </div>
        </div>
      )}

      {/* ── Scene Picker Modal ────────────────────────────────────── */}
      {showScenePicker && (
        <ScenePickerModal
          projectId={projectId}
          token={token}
          existingSceneIds={new Set((sheet.scenes || []).map(s => s.scene_id))}
          onAdd={handleScenesAdded}
          onClose={() => setShowScenePicker(false)}
        />
      )}

      {/* ── Share Modal ───────────────────────────────────────────── */}
      {showShare && (
        <ShareModal
          sheet={sheet}
          projectId={projectId}
          sheetId={sheetId}
          token={token}
          onClose={() => setShowShare(false)}
        />
      )}

      <PdfLanguageDialog
        open={showPdfLanguageDialog}
        onClose={() => setShowPdfLanguageDialog(false)}
        onConfirm={async (synopsisLang) => {
          setShowPdfLanguageDialog(false);
          await downloadPdf(synopsisLang);
        }}
        loading={downloadingPdf}
        title="Choose call sheet PDF synopsis language"
      />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  Section Components
// ═══════════════════════════════════════════════════════════════════════════

// ── 1. Header ────────────────────────────────────────────────────────────────
function HeaderSection({ sheet, update, updateNested, isPublished, previewMode }) {
  const productionTeam = sheet.production_team || [];

  function addProductionTeamRole() {
    update('production_team', [...productionTeam, { id: crypto.randomUUID(), role: '', name: '' }]);
  }

  function updateProductionTeam(id, field, value) {
    update('production_team', productionTeam.map(item => item.id === id ? { ...item, [field]: value } : item));
  }

  function removeProductionTeam(id) {
    update('production_team', productionTeam.filter(item => item.id !== id));
  }

  return (
    <div>
      <div className="rounded-2xl overflow-hidden"
           style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
        {/* Top accent row */}
        <div className="px-6 py-4 flex items-center justify-between"
             style={{ background: 'var(--cb-bg)', borderBottom: '1px solid var(--cb-border)' }}>
          <div className="flex items-center gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold mb-0.5" style={{ color: 'var(--cb-text-dim)' }}>
                Shooting Day
              </div>
              <div className="text-4xl font-black font-mono" style={{ color: 'var(--cb-accent)' }}>
                {sheet.shooting_day}
                <span className="text-lg font-normal ml-2" style={{ color: 'var(--cb-text-dim)' }}>of {sheet.total_shooting_days}</span>
              </div>
            </div>
            <div className="h-12 w-px" style={{ background: 'var(--cb-border)' }} />
            <div>
              <div className="text-xs uppercase tracking-widest font-bold mb-0.5" style={{ color: 'var(--cb-text-dim)' }}>Crew Call</div>
              <div className="text-2xl font-black font-mono" style={{ color: 'var(--cb-text)' }}>
                <EField value={sheet.crew_call} onChange={v => update('crew_call', v)} placeholder="07:00" mono />
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest font-bold mb-0.5" style={{ color: 'var(--cb-text-dim)' }}>Status</div>
            <span className="text-sm font-bold px-3 py-1 rounded-full"
                  style={{
                    background: isPublished ? 'rgba(34,197,94,0.15)' : 'var(--cb-accent-dim)',
                    color: isPublished ? '#22C55E' : 'var(--cb-accent)',
                  }}>
              {isPublished ? 'Published' : 'Draft'} · {sheet.version_label || 'v1'}
            </span>
          </div>
        </div>

        {/* Production info grid */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          {[
            { label: 'Production Name',         field: 'production_name',     placeholder: 'The Great Film' },
            { label: 'Production Company',       field: 'production_company',  placeholder: 'Studio Name' },
            { label: 'Director',                 field: 'director',            placeholder: 'Name' },
            { label: 'Producer',                 field: 'producer',            placeholder: 'Name' },
            { label: 'Executive Producer',       field: 'executive_producer',  placeholder: 'Name' },
            { label: 'DOP / Cinematographer',    field: 'dop',                 placeholder: 'Name' },
            { label: '1st Assistant Director',   field: 'first_ad',            placeholder: 'Name' },
            { label: 'Line Producer',            field: 'line_producer',       placeholder: 'Name' },
            { label: 'Unit',                     field: 'unit',                placeholder: 'A Unit' },
          ].map(({ label, field, placeholder }) => (
            <div key={field} data-testid={`overview-field-${field}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
                   style={{ color: 'var(--cb-text-dim)' }}>{label}</div>
              <EField value={sheet[field]} onChange={v => update(field, v)} placeholder={placeholder} />
            </div>
          ))}
        </div>

        <div className="px-6 pb-6">
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--cb-border)', background: 'var(--cb-bg)' }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--cb-border)' }}>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--cb-text-dim)' }}>Production Team</div>
                <div className="text-sm mt-1" style={{ color: 'var(--cb-text-sec)' }}>Add unlimited custom roles and names for the call sheet and PDF.</div>
              </div>
              {!previewMode && (
                <Button size="sm" variant="ghost" onClick={addProductionTeamRole} className="gap-1.5 text-xs" style={{ color: 'var(--cb-accent)' }} data-testid="add-production-team-role-button">
                  <Plus className="h-3.5 w-3.5" /> Add Role
                </Button>
              )}
            </div>
            <div className="px-4 py-3 text-xs" style={{ color: 'var(--cb-text-sec)' }}>
              Key contacts stay editable above. Use this area for additional custom roles like Costume Head, Makeup Head, Art Director, Production Manager, Sound Designer, or Transport Captain.
            </div>
            <div className="px-4 pb-4 space-y-3">
              {productionTeam.length === 0 ? (
                <div className="rounded-xl px-4 py-4 text-sm" style={{ background: 'var(--cb-surface)', border: '1px dashed var(--cb-border)', color: 'var(--cb-text-dim)' }}>
                  No custom production roles yet.
                </div>
              ) : productionTeam.map((member) => (
                <div key={member.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-center rounded-xl p-3" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }} data-testid={`production-team-row-${member.id}`}>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--cb-text-dim)' }}>Role</div>
                    <EField value={member.role} onChange={v => updateProductionTeam(member.id, 'role', v)} placeholder="Costume Designer" small />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--cb-text-dim)' }}>Name</div>
                    <EField value={member.name} onChange={v => updateProductionTeam(member.id, 'name', v)} placeholder="Person Name" small />
                  </div>
                  {!previewMode && (
                    <button onClick={() => removeProductionTeam(member.id)} className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ color: '#EF4444', border: '1px solid var(--cb-border)' }} data-testid={`remove-production-team-role-${member.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Weather / date row */}
        <div className="px-6 pb-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { label: 'Date',        field: 'date',      placeholder: 'YYYY-MM-DD' },
            { label: 'Day / Night', field: 'day_night', placeholder: 'Day' },
            { label: 'Weather',     field: 'weather',   placeholder: 'Sunny 28°C' },
            { label: 'Sunrise',     field: 'sunrise',   placeholder: '06:12', mono: true },
            { label: 'Sunset',      field: 'sunset',    placeholder: '18:45', mono: true },
          ].map(({ label, field, placeholder, mono }) => (
            <div key={field} className="px-3 py-2 rounded-lg"
                 style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)' }}>
              <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--cb-text-dim)' }}>
                {label}
              </div>
              <EField value={sheet[field]} onChange={v => update(field, v)} placeholder={placeholder} mono={mono} small />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 2. Emergency ──────────────────────────────────────────────────────────────
function EmergencySection({ sheet, update }) {
  const contacts = sheet.emergency_contacts || [];
  const hospital = sheet.nearest_hospital || {};

  function addContact() {
    update('emergency_contacts', [...contacts, { id: crypto.randomUUID(), role: '', name: '', phone: '', email: '' }]);
  }
  function updateContact(idx, field, val) {
    update('emergency_contacts', contacts.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  }
  function removeContact(idx) {
    update('emergency_contacts', contacts.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <SectionHeader id="emergency" icon={AlertTriangle} title="Emergency Information" accent="#EF4444" />
      <div className="rounded-xl p-4 mb-4" style={{ background: '#7F1D1D', border: '1px solid #DC2626' }}>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          <span className="text-sm font-bold text-red-200 uppercase tracking-widest">Emergency Contacts</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {contacts.map((c, i) => (
            <div key={c.id || i} className="group relative p-2 rounded-lg bg-red-950/50">
              <button onClick={() => removeContact(i)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-400">
                <X className="h-3 w-3" />
              </button>
              <input value={c.role} onChange={e => updateContact(i, 'role', e.target.value)}
                     placeholder="Role" className="w-full bg-transparent outline-none text-xs font-bold text-red-300 mb-0.5" />
              <input value={c.name} onChange={e => updateContact(i, 'name', e.target.value)}
                     placeholder="Name" className="w-full bg-transparent outline-none text-xs text-red-200 mb-0.5" />
              <input value={c.phone} onChange={e => updateContact(i, 'phone', e.target.value)}
                     placeholder="Phone" className="w-full bg-transparent outline-none text-xs font-mono text-red-100 font-bold" />
            </div>
          ))}
          <button onClick={addContact}
                  className="p-2 rounded-lg border border-dashed flex items-center justify-center gap-1.5 text-xs text-red-400 border-red-800 hover:border-red-600">
            <Plus className="h-3.5 w-3.5" /> Add Contact
          </button>
        </div>
      </div>

      <div className="rounded-xl p-4" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
        <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--cb-text-dim)' }}>
          Nearest Hospital
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Hospital Name', field: 'name' },
            { label: 'Address',       field: 'address' },
            { label: 'Phone',         field: 'phone' },
            { label: 'ETA / Distance',field: 'eta' },
          ].map(({ label, field }) => (
            <div key={field}>
              <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--cb-text-dim)' }}>{label}</div>
              <input value={hospital[field] || ''} onChange={e => update('nearest_hospital', { ...hospital, [field]: e.target.value })}
                     placeholder={label} className="w-full text-sm bg-transparent outline-none border-b"
                     style={{ color: 'var(--cb-text)', borderColor: 'var(--cb-border)' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 3. Summary KPIs ──────────────────────────────────────────────────────────
function SummarySection({ sheet, totalPages, update, previewMode }) {
  const scenes    = sheet.scenes || [];
  const castCount = sheet.cast_call_times?.length || 0;
  const crewCount = (sheet.crew_departments || []).reduce((a, d) => a + (d.members?.length || 0), 0);
  const locCount  = sheet.locations?.length || 0;
  const computedSummary = {
    shooting_day: `${sheet.shooting_day}/${sheet.total_shooting_days}`,
    total_scenes: String(scenes.length),
    script_pages: `${totalPages.toFixed(2)}p`,
    cast_working: String(castCount),
    crew_count: String(crewCount || 0),
    locations_count: String(locCount),
    crew_call: sheet.crew_call || '',
    unit: sheet.unit || '',
  };
  const summary = { ...computedSummary, ...(sheet.production_summary || {}) };

  function updateSummary(key, value) {
    update('production_summary', {
      ...summary,
      [key]: value,
    });
  }

  return (
    <div>
      <SectionHeader id="summary" icon={CheckCircle2} title="Production Summary" />
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {SUMMARY_FIELDS.map(({ key, label, icon: Icon, color }) => (
          <div key={label} className="rounded-xl p-3 text-center"
               style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}
               data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <Icon className="h-4 w-4 mx-auto mb-2" style={{ color }} />
            <div className="text-lg font-black font-mono" style={{ color: 'var(--cb-text)' }}>
              <EField value={summary[key] || ''} onChange={v => updateSummary(key, v)} placeholder={computedSummary[key] || '—'} mono small />
            </div>
            <div className="text-[9px] uppercase tracking-widest mt-0.5" style={{ color: 'var(--cb-text-dim)' }}>{label}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-2xl p-4" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--cb-text-dim)' }}>Editable Production Summary Notes</div>
        <textarea
          value={sheet.general_notes || ''}
          readOnly={previewMode}
          onChange={e => update('general_notes', e.target.value)}
          className="w-full text-sm bg-transparent outline-none resize-none rounded-xl px-3 py-3"
          style={{ color: 'var(--cb-text)', border: '1px solid var(--cb-border)', minHeight: '120px' }}
          placeholder="Add full production summary, shoot-day notes, working method, special instructions…"
          data-testid="production-summary-notes"
        />
      </div>
    </div>
  );
}

// ── 4. Timeline ───────────────────────────────────────────────────────────────
function TimelineSection({ sheet, updateNested }) {
  const timeline = sheet.timeline || {};
  return (
    <div>
      <SectionHeader id="timeline" icon={Clock} title="Schedule Timeline" />
      <div className="rounded-xl p-5 overflow-x-auto"
           style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
        <div className="min-w-[680px]">
          <div className="flex items-start gap-0">
            {TIMELINE_MILESTONES.map((m, i) => {
              const time = timeline[m.key] || '';
              const isSet = !!time;
              return (
                <div key={m.key} className="flex-1 flex flex-col items-center relative">
                  {i < TIMELINE_MILESTONES.length - 1 && (
                    <div className="absolute top-3 left-1/2 w-full h-0.5" style={{ background: 'var(--cb-border)', zIndex: 0 }} />
                  )}
                  <div className="relative z-10 h-6 w-6 rounded-full flex items-center justify-center mb-2"
                       style={{ background: isSet ? m.color : 'var(--cb-surface-a)', border: `2px solid ${isSet ? m.color : 'var(--cb-border)'}` }}>
                    {isSet && <div className="h-2 w-2 rounded-full bg-white" />}
                  </div>
                  <div className="text-[10px] text-center mb-1 leading-tight px-1"
                       style={{ color: isSet ? m.color : 'var(--cb-text-dim)' }}>
                    {m.label}
                  </div>
                  <input type="time" value={time}
                         onChange={e => updateNested('timeline', m.key, e.target.value)}
                         className="w-full text-center text-xs font-mono bg-transparent outline-none rounded px-1 py-0.5"
                         style={{ color: isSet ? 'var(--cb-text)' : 'var(--cb-text-dim)', border: `1px solid ${isSet ? m.color : 'var(--cb-border)'}`, fontSize: '10px' }} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 5. Scenes ─────────────────────────────────────────────────────────────────
function ScenesSection({ sheet, update, onDragEnd, onOpenPicker, onOpenLocations, previewMode }) {
  const scenes = sheet.scenes || [];
  const locationGroups = buildLocationSceneGroups(scenes);

  function removeScene(id) { update('scenes', scenes.filter(s => s.id !== id)); }
  function updateScene(id, field, value) {
    update('scenes', scenes.map(s => s.id === id ? { ...s, [field]: value } : s));
  }

  function applyLocationsFromManager() {
    const locations = sheet.locations || [];
    if (!locations.length) {
      toast.error('Add locations first in Location Management');
      return;
    }
    update('scenes', scenes.map((scene, index) => {
      const matchingLocation = locations.find(loc => (loc.name || '').trim().toLowerCase() === (scene.real_location || '').trim().toLowerCase());
      if (matchingLocation) return { ...scene, order: index };
      const autoMatch = locations.find(loc => (loc.name || '').trim().toLowerCase() === (scene.location || '').trim().toLowerCase());
      return { ...scene, real_location: autoMatch?.name || scene.real_location || '', order: index };
    }));
    toast.success('Mapped call sheet locations into scene rows');
  }

  function groupScenesByLocation() {
    const groupedScenes = buildLocationSceneGroups(scenes).flatMap(group => group.scenes);
    update('scenes', groupedScenes.map((scene, index) => ({ ...scene, order: index })));
    toast.success('Scenes grouped by location');
  }

  const sorted = [...scenes].sort((a, b) => (a.order || 0) - (b.order || 0));
  const rows = [];
  let lastRealLoc = null;
  const locationColors = ['#6366F1', '#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444', '#14B8A6'];
  const locColorMap = {};
  let colorIdx = 0;

  sorted.forEach((scene, i) => {
    const rl = (scene.real_location || '').trim();
    if (rl && rl !== lastRealLoc) {
      if (!locColorMap[rl]) { locColorMap[rl] = locationColors[colorIdx % locationColors.length]; colorIdx++; }
      rows.push({ type: 'separator', location: rl, color: locColorMap[rl] });
      lastRealLoc = rl;
    }
    rows.push({ type: 'scene', scene, index: i });
  });

  const GRID = '24px 32px 40px 40px 48px 120px 120px minmax(0,2fr) 110px 44px';

  return (
    <div>
      <SectionHeader id="scenes" icon={FileText} title={`Scenes to be Shot (${scenes.length})`}
                     action={
                       !previewMode && (
                         <div className="flex items-center gap-2 flex-wrap justify-end">
                           <Button size="sm" variant="ghost" onClick={() => { applyLocationsFromManager(); onOpenLocations?.(); }}
                                   className="gap-1.5 text-xs font-bold"
                                   style={{ color: '#10B981', background: 'rgba(16,185,129,0.12)' }}
                                   data-testid="apply-location-management-button">
                             <MapPin className="h-3.5 w-3.5" /> Location Management
                           </Button>
                           <Button size="sm" variant="ghost" onClick={groupScenesByLocation}
                                   className="gap-1.5 text-xs font-bold"
                                   style={{ color: '#6366F1', background: 'rgba(99,102,241,0.12)' }}
                                   data-testid="group-scenes-location-button">
                             <MapPin className="h-3.5 w-3.5" /> Group by Location
                           </Button>
                           <Button size="sm" variant="ghost" onClick={onOpenPicker}
                                   className="gap-1.5 text-xs font-bold"
                                   style={{ color: 'var(--cb-accent)', background: 'var(--cb-accent-dim)' }}
                                   data-testid="add-scene-btn">
                             <Plus className="h-3.5 w-3.5" /> Add from Breakdown
                           </Button>
                         </div>
                       )
                     } />

      <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {locationGroups.map((group, index) => (
          <div key={`${group.key}-${index}`} className="rounded-xl p-3" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }} data-testid={`scene-location-group-${index}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--cb-text-dim)' }}>Location Group</div>
            <div className="text-sm font-bold" style={{ color: 'var(--cb-text)' }}>{group.name}</div>
            <div className="text-xs mt-1" style={{ color: 'var(--cb-text-sec)' }}>{group.sceneCount} scene(s) · {group.totalPages.toFixed(2)} pages</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--cb-border)' }}>
        {/* Table header */}
        <div className="grid text-[10px] font-bold uppercase tracking-widest px-3 py-2"
             style={{
               background: 'var(--cb-bg)', color: 'var(--cb-text-dim)',
               gridTemplateColumns: GRID,
               borderBottom: '1px solid var(--cb-border)'
             }}>
          <span></span><span>#</span><span>I/E</span>
          <span>D/N</span><span>PGS</span>
          <span>Script Location</span>
          <span>Real Location</span>
          <span>Synopsis</span>
          <span>Cast</span><span></span>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="scenes-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {rows.map((row, ri) => {
                  if (row.type === 'separator') {
                    const matchingGroup = locationGroups.find(group => group.name === row.location || group.key === row.location);
                    return (
                      <div key={`sep-${row.location}-${ri}`}
                           className="flex items-center justify-between gap-2 px-4 py-2"
                           style={{ background: `${row.color}18`, borderBottom: `2px solid ${row.color}55` }}>
                        <div className="flex items-center gap-2 min-w-0">
                          <MapPin className="h-3 w-3 shrink-0" style={{ color: row.color }} />
                          <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: row.color }}>
                            Location: {row.location}
                          </span>
                        </div>
                        <span className="text-[10px] font-bold" style={{ color: row.color }}>
                          {matchingGroup?.sceneCount || 0} scenes · {(matchingGroup?.totalPages || 0).toFixed(2)} pages
                        </span>
                      </div>
                    );
                  }

                  const { scene, index: i } = row;
                  return (
                    <Draggable key={scene.id} draggableId={scene.id} index={i}>
                      {(prov, snap) => (
                        <div ref={prov.innerRef} {...prov.draggableProps}
                             className="group"
                             style={{
                               ...prov.draggableProps.style,
                               background: snap.isDragging ? 'var(--cb-surface-h)' : (i % 2 === 0 ? 'var(--cb-surface)' : 'var(--cb-bg)'),
                               borderBottom: '1px solid var(--cb-border)',
                               display: 'grid',
                               gridTemplateColumns: GRID,
                               alignItems: 'start',
                               padding: '7px 12px',
                             }}>
                          {previewMode ? <span /> : (
                            <div {...prov.dragHandleProps} className="flex items-center justify-center pt-1 opacity-0 group-hover:opacity-100 cursor-grab">
                              <GripVertical className="h-4 w-4" style={{ color: 'var(--cb-text-dim)' }} />
                            </div>
                          )}

                          <input value={scene.scene_number ?? ''}
                                 readOnly={previewMode}
                                 onChange={e => updateScene(scene.id, 'scene_number', e.target.value === '' ? null : e.target.value)}
                                 className="text-xs font-mono font-bold bg-transparent outline-none w-full"
                                 style={{ color: 'var(--cb-accent)' }} placeholder="#" />

                          <select value={scene.int_ext} disabled={previewMode}
                                  onChange={e => updateScene(scene.id, 'int_ext', e.target.value)}
                                  className="text-xs bg-transparent outline-none" style={{ color: 'var(--cb-text-sec)' }}>
                            <option>INT</option><option>EXT</option><option>INT/EXT</option>
                          </select>

                          <select value={scene.day_night} disabled={previewMode}
                                  onChange={e => updateScene(scene.id, 'day_night', e.target.value)}
                                  className="text-xs bg-transparent outline-none" style={{ color: 'var(--cb-text-sec)' }}>
                            <option>DAY</option><option>NIGHT</option><option>DUSK</option><option>DAWN</option>
                          </select>

                          <input value={scene.pages || ''} type="number" step="0.125" min="0"
                                 readOnly={previewMode}
                                 onChange={e => updateScene(scene.id, 'pages', parseFloat(e.target.value) || 0)}
                                 className="text-xs font-mono bg-transparent outline-none w-full"
                                 style={{ color: 'var(--cb-text)' }} placeholder="0" />

                          <input value={scene.location}
                                 readOnly={previewMode}
                                 onChange={e => updateScene(scene.id, 'location', e.target.value)}
                                 className="text-xs bg-transparent outline-none w-full font-medium"
                                 style={{ color: 'var(--cb-text)' }} placeholder="Script Location" />

                          <div>
                            <input value={scene.real_location || ''}
                                   readOnly={previewMode}
                                   onChange={e => updateScene(scene.id, 'real_location', e.target.value)}
                                   className="text-xs bg-transparent outline-none w-full"
                                   style={{
                                     color: scene.real_location ? 'var(--cb-accent)' : 'var(--cb-text-dim)',
                                     borderBottom: previewMode ? 'none' : `1px solid ${scene.real_location ? 'rgba(232,200,71,0.3)' : 'var(--cb-border)'}`,
                                   }}
                                   placeholder={previewMode ? '—' : 'Actual shoot location'} />
                          </div>

                          <textarea value={scene.description}
                                    readOnly={previewMode}
                                    onChange={e => updateScene(scene.id, 'description', e.target.value)}
                                    className="text-xs bg-transparent outline-none w-full resize-none leading-snug"
                                    style={{ color: 'var(--cb-text-sec)' }} rows={3}
                                    placeholder="Synopsis…"
                                    data-testid={`scene-synopsis-${scene.id}`} />

                          <textarea value={(scene.cast || []).join(', ')}
                                    readOnly={previewMode}
                                    onChange={e => updateScene(scene.id, 'cast', e.target.value.split(',').map(x => x.trim()).filter(Boolean))}
                                    className="text-xs bg-transparent outline-none w-full resize-none"
                                    style={{ color: 'var(--cb-text-sec)' }} rows={2}
                                    placeholder="Characters…" />

                          <div className="flex justify-end pt-0.5">
                            {!previewMode && (
                              <button onClick={() => removeScene(scene.id)}
                                      className="opacity-0 group-hover:opacity-100" style={{ color: '#EF4444' }}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {scenes.length === 0 && (
          <div className="text-center py-10" style={{ color: 'var(--cb-text-dim)' }}>
            <FileText className="h-8 w-8 mx-auto mb-3 opacity-30" />
            <p className="text-sm mb-3">No scenes added yet</p>
            {!previewMode && (
              <Button size="sm" onClick={onOpenPicker} style={{ background: 'var(--cb-accent)', color: '#000' }}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add from Breakdown
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── 6. Cast ───────────────────────────────────────────────────────────────────
function castTimeCheck(ct) {
  const pickup  = ct.pickup_call || '';
  const onSet   = ct.on_set_call || '';
  if (!pickup && !onSet) return null;

  // Convert HH:MM → total minutes
  const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

  // Conflict: On Set is before Pickup
  if (pickup && onSet) {
    const pm = toMin(pickup); const sm = toMin(onSet);
    if (sm < pm) return { type: 'conflict', msg: 'On Set is BEFORE Pickup time' };
  }
  // Suspicious: Pickup before 5 AM (likely typo)
  if (pickup) {
    const pm = toMin(pickup);
    if (pm < 300) return { type: 'warn', msg: `Pickup at ${pickup} — unusual, please verify` };
  }
  return null;
}

function CastSection({ sheet, update, previewMode }) {
  const cast   = sheet.cast_call_times || [];
  const scenes = sheet.scenes || [];
  const STATUS_COLORS = {
    'Confirmed': '#22C55E', 'Pending': '#F59E0B', 'Changed': '#EF4444', 'N/A': '#6B7280',
  };

  function addCast() {
    const n = { id: crypto.randomUUID(), character: '', actor_name: '',
                pickup_call: '', on_set_call: '', contact: '', status: 'Pending' };
    update('cast_call_times', [...cast, n]);
  }
  function removeCast(id) { update('cast_call_times', cast.filter(c => c.id !== id)); }
  function updateCast(id, f, v) { update('cast_call_times', cast.map(c => c.id === id ? {...c, [f]: v} : c)); }

  function importFromScenes() {
    const existingNames = new Set(cast.map(c => c.character?.toLowerCase()));
    const newEntries = [];
    scenes.forEach(sc => {
      (sc.cast || []).forEach(name => {
        if (name && !existingNames.has(name.toLowerCase())) {
          existingNames.add(name.toLowerCase());
          newEntries.push({ id: crypto.randomUUID(), character: name, actor_name: '',
                            pickup_call: '', on_set_call: '', contact: '', status: 'Pending' });
        }
      });
    });
    if (newEntries.length > 0) update('cast_call_times', [...cast, ...newEntries]);
  }

  const sceneCastCount = new Set(scenes.flatMap(sc => sc.cast || [])).size;
  const missingCount   = sceneCastCount - cast.length;

  // Count cards with issues for banner
  const conflicts = cast.map(c => castTimeCheck(c)).filter(Boolean);
  const errorCount = conflicts.filter(c => c.type === 'conflict').length;
  const warnCount  = conflicts.filter(c => c.type === 'warn').length;

  return (
    <div>
      <SectionHeader id="cast" icon={Users}
                     title={`Cast Call Times (${cast.length})${errorCount > 0 ? ` · ${errorCount} error${errorCount>1?'s':''}` : ''}`}
                     action={
                       !previewMode && (
                         <div className="flex items-center gap-2">
                           {missingCount > 0 && (
                             <Button size="sm" variant="ghost" onClick={importFromScenes}
                                     className="gap-1.5 text-xs" style={{ color: '#10B981' }}>
                               <Plus className="h-3.5 w-3.5" /> Import from Scenes ({missingCount})
                             </Button>
                           )}
                           <Button size="sm" variant="ghost" onClick={addCast}
                                   className="gap-1.5 text-xs" style={{ color: 'var(--cb-accent)' }}>
                             <Plus className="h-3.5 w-3.5" /> Add Cast
                           </Button>
                         </div>
                       )
                     } />

      {/* Summary error/warn banner */}
      {(errorCount > 0 || warnCount > 0) && !previewMode && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl mb-3"
             style={{
               background: errorCount > 0 ? 'rgba(239,68,68,0.07)' : 'rgba(245,158,11,0.07)',
               border: `1px solid ${errorCount > 0 ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)'}`,
             }}>
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5"
                         style={{ color: errorCount > 0 ? '#EF4444' : '#F59E0B' }} />
          <div className="text-xs leading-relaxed" style={{ color: errorCount > 0 ? '#EF4444' : '#F59E0B' }}>
            {errorCount > 0 && (
              <span className="font-bold">{errorCount} cast member{errorCount > 1 ? 's have' : ' has'} On Set time earlier than Pickup. </span>
            )}
            {warnCount > 0 && (
              <span>{warnCount} suspicious pickup time{warnCount > 1 ? 's' : ''} (before 5 AM). </span>
            )}
            <span className="opacity-70">Review highlighted cards below before generating PDF.</span>
          </div>
        </div>
      )}

      {cast.length === 0 ? (
        <div className="text-center py-10 rounded-xl"
             style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', color: 'var(--cb-text-dim)' }}>
          <Users className="h-7 w-7 mx-auto mb-2 opacity-30" />
          <p className="text-sm mb-2">Cast is auto-added when you sync or pick scenes</p>
          {!previewMode && sceneCastCount > 0 && (
            <Button size="sm" onClick={importFromScenes} style={{ background: 'var(--cb-accent)', color: '#000' }}>
              Import {sceneCastCount} cast from scenes
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {cast.map(ct => {
            const sColor = STATUS_COLORS[ct.status] || '#6B7280';
            const issue  = castTimeCheck(ct);
            const isErr  = issue?.type === 'conflict';
            const isWarn = issue?.type === 'warn';
            const cardBorder = isErr ? '#EF4444' : isWarn ? '#F59E0B' : 'var(--cb-border)';

            return (
              <div key={ct.id} className="rounded-xl overflow-hidden group"
                   style={{
                     background: 'var(--cb-surface)',
                     border: `1px solid ${cardBorder}`,
                     boxShadow: isErr ? '0 0 0 2px rgba(239,68,68,0.1)' : isWarn ? '0 0 0 2px rgba(245,158,11,0.1)' : 'none',
                   }}
                   data-testid={`cast-card-${ct.id}`}>
                {/* Top color strip */}
                <div className="h-0.5" style={{ background: isErr ? '#EF4444' : isWarn ? '#F59E0B' : sColor }} />

                <div className="p-4">
                  {/* Character + Actor row */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <input value={ct.character} readOnly={previewMode}
                             onChange={e => updateCast(ct.id, 'character', e.target.value)}
                             className="w-full text-sm font-bold bg-transparent outline-none"
                             style={{ color: 'var(--cb-accent)' }} placeholder="Character" />
                      <input value={ct.actor_name || ''} readOnly={previewMode}
                             onChange={e => updateCast(ct.id, 'actor_name', e.target.value)}
                             className="w-full text-xs bg-transparent outline-none"
                             style={{ color: 'var(--cb-text-sec)' }} placeholder="Actor Name" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      {!previewMode && (
                        <select value={ct.status} onChange={e => updateCast(ct.id, 'status', e.target.value)}
                                className="text-[10px] font-bold rounded px-1.5 py-0.5 outline-none"
                                style={{ background: `${sColor}22`, color: sColor, border: `1px solid ${sColor}44` }}>
                          {['Confirmed','Pending','Changed','N/A'].map(s => <option key={s}>{s}</option>)}
                        </select>
                      )}
                      {!previewMode && (
                        <button onClick={() => removeCast(ct.id)} className="opacity-0 group-hover:opacity-100" style={{ color: '#EF4444' }}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Time fields */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {[
                      { label: 'Pickup',  field: 'pickup_call' },
                      { label: 'On Set',  field: 'on_set_call' },
                    ].map(({ label, field }) => {
                      const isErrorField = isErr && field === 'on_set_call';
                      const isWarnField  = isWarn && field === 'pickup_call';
                      return (
                        <div key={field}>
                          <div className="text-[9px] uppercase tracking-widest mb-0.5"
                               style={{ color: isErrorField ? '#EF4444' : isWarnField ? '#F59E0B' : 'var(--cb-text-dim)' }}>
                            {label} {isErrorField && '⚠'} {isWarnField && '⚠'}
                          </div>
                          <input type="time" value={ct[field] || ''} readOnly={previewMode}
                                 onChange={e => updateCast(ct.id, field, e.target.value)}
                                 className="w-full text-sm font-mono font-bold bg-transparent outline-none border-b"
                                 style={{
                                   color: isErrorField ? '#EF4444' : isWarnField ? '#F59E0B' : ct[field] ? 'var(--cb-text)' : 'var(--cb-text-dim)',
                                   borderColor: isErrorField ? '#EF4444' : isWarnField ? '#F59E0B' : 'var(--cb-border)',
                                 }} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Issue message */}
                  {issue && (
                    <div className="flex items-center gap-1.5 mt-2.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                         style={{
                           background: isErr ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                           color: isErr ? '#EF4444' : '#F59E0B',
                           border: `1px solid ${isErr ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
                         }}>
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      {issue.msg}
                    </div>
                  )}

                  {/* Contact */}
                  {!previewMode && (
                    <div className="mt-3 flex items-center gap-2" style={{ color: 'var(--cb-text-dim)' }}>
                      <Phone className="h-3 w-3 shrink-0" />
                      <input value={ct.contact || ''} onChange={e => updateCast(ct.id, 'contact', e.target.value)}
                             className="flex-1 bg-transparent outline-none text-xs"
                             style={{ color: 'var(--cb-text-sec)' }} placeholder="Contact number" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 7. Crew — Call Time Slots ─────────────────────────────────────────────────
const ALL_DEPARTMENTS = [
  'Production','Direction','Camera','Sound','Lighting','Grip',
  'Art Department','Costume','Makeup & Hair','Transport','VFX','Stunts',
];

function CrewSection({ sheet, update, previewMode }) {
  const slots = sheet.crew_call_slots || [];
  const depts = sheet.crew_departments || [];
  const productionTeam = sheet.production_team || [];

  // Which departments are already assigned to a slot
  const assignedDepts = new Set(slots.flatMap(s => s.departments || []));
  const unassignedDepts = ALL_DEPARTMENTS.filter(d => !assignedDepts.has(d));

  function addSlot() {
    const newSlot = { id: crypto.randomUUID(), time: '', departments: [] };
    update('crew_call_slots', [...slots, newSlot]);
  }

  function removeSlot(id) {
    update('crew_call_slots', slots.filter(s => s.id !== id));
  }

  function updateSlotTime(id, time) {
    update('crew_call_slots', slots.map(s => s.id === id ? { ...s, time } : s));
  }

  function toggleDeptInSlot(slotId, dept) {
    update('crew_call_slots', slots.map(s => {
      if (s.id !== slotId) return s;
      const has = (s.departments || []).includes(dept);
      return { ...s, departments: has ? s.departments.filter(d => d !== dept) : [...(s.departments || []), dept] };
    }));
  }

  function updateDeptHead(deptName, value) {
    const existing = depts.find(d => d.department === deptName);
    if (existing) {
      update('crew_departments', depts.map(d => d.department === deptName ? { ...d, dept_head: value } : d));
    } else {
      update('crew_departments', [...depts, { department: deptName, color: DEPT_COLORS[deptName] || '#6366F1', dept_head: value, dept_call_time: '', dept_notes: '', members: [] }]);
    }
  }

  function getDeptHead(deptName) {
    return depts.find(d => d.department === deptName)?.dept_head || '';
  }

  function addCustomDepartment() {
    const customName = `Custom Role ${depts.length + 1}`;
    update('crew_departments', [...depts, { department: customName, color: '#94A3B8', dept_head: '', dept_call_time: '', dept_notes: '', members: [] }]);
  }

  return (
    <div>
      <SectionHeader id="crew" icon={Camera} title="Crew Call Times"
                     action={!previewMode && (
                       <Button size="sm" variant="ghost" onClick={addCustomDepartment} className="gap-1.5 text-xs" style={{ color: 'var(--cb-accent)' }} data-testid="add-custom-department-button">
                         <Plus className="h-3.5 w-3.5" /> Add Custom Department
                       </Button>
                     )} />

      {productionTeam.length > 0 && (
        <div className="mb-4 rounded-xl p-4" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
          <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--cb-text-dim)' }}>Production Team Directory</div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {productionTeam.map((member) => (
              <div key={member.id} className="rounded-xl px-3 py-2" style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)' }}>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'var(--cb-text-dim)' }}>{member.role || 'Custom Role'}</div>
                <div className="text-sm mt-1" style={{ color: 'var(--cb-text)' }}>{member.name || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {slots.length === 0 && !previewMode ? (
        <div className="text-center py-8 rounded-xl"
             style={{ background: 'var(--cb-surface)', border: '1px dashed var(--cb-border)' }}>
          <Clock className="h-7 w-7 mx-auto mb-2 opacity-30" style={{ color: 'var(--cb-text-dim)' }} />
          <p className="text-sm mb-3" style={{ color: 'var(--cb-text-dim)' }}>No crew call times set</p>
          <Button size="sm" onClick={addSlot}
                  data-testid="add-crew-slot-btn"
                  style={{ background: 'var(--cb-accent)', color: '#000', fontWeight: 700 }}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Call Time
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {slots.map((slot, si) => {
            const slotDepts = slot.departments || [];
            // Departments available for this slot = already in slot + unassigned (no duplicates)
            const availableForSlot = ALL_DEPARTMENTS.filter(d => !assignedDepts.has(d) || slotDepts.includes(d));

            return (
              <div key={slot.id} className="rounded-xl overflow-hidden"
                   style={{ border: '1px solid var(--cb-border)' }}>
                {/* Slot header row */}
                <div className="flex items-center gap-3 px-4 py-3"
                     style={{ background: 'var(--cb-bg)', borderBottom: '1px solid var(--cb-border)' }}>
                  <div className="flex items-center gap-2 shrink-0">
                    <Clock className="h-4 w-4" style={{ color: 'var(--cb-accent)' }} />
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--cb-text-dim)' }}>
                      Call Time {si + 1}
                    </span>
                  </div>
                  <input type="time" value={slot.time || ''}
                         readOnly={previewMode}
                         onChange={e => updateSlotTime(slot.id, e.target.value)}
                         className="text-lg font-black font-mono bg-transparent outline-none"
                         style={{ color: 'var(--cb-accent)', minWidth: '90px' }} />
                  <div className="flex-1" />
                  {!previewMode && (
                    <button onClick={() => removeSlot(slot.id)} className="opacity-40 hover:opacity-100 transition-opacity"
                            style={{ color: '#EF4444' }}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Department chips */}
                <div className="px-4 py-3" style={{ background: 'var(--cb-surface)' }}>
                  <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--cb-text-dim)' }}>
                    Departments on this call
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableForSlot.map(dept => {
                      const color = DEPT_COLORS[dept] || '#6366F1';
                      const active = slotDepts.includes(dept);
                      return (
                        <button
                          key={dept}
                          onClick={() => !previewMode && toggleDeptInSlot(slot.id, dept)}
                          disabled={previewMode}
                          className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                          style={{
                            background: active ? `${color}22` : 'var(--cb-bg)',
                            color: active ? color : 'var(--cb-text-dim)',
                            border: `1px solid ${active ? color : 'var(--cb-border)'}`,
                            fontWeight: active ? 700 : 400,
                            cursor: previewMode ? 'default' : 'pointer',
                          }}
                          data-testid={`dept-chip-${dept.replace(/\s/g,'-')}`}>
                          {active && <span className="mr-1">✓</span>}{dept}
                        </button>
                      );
                    })}
                  </div>

                  {/* Dept heads for selected departments */}
                  {slotDepts.length > 0 && (
                    <div className="mt-3 pt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2"
                         style={{ borderTop: '1px solid var(--cb-border)' }}>
                      {slotDepts.map(dept => {
                        const color = DEPT_COLORS[dept] || '#6366F1';
                        return (
                          <div key={dept}>
                            <div className="text-[9px] uppercase tracking-widest font-bold mb-0.5" style={{ color }}>
                              {dept}
                            </div>
                            <input value={getDeptHead(dept)}
                                   readOnly={previewMode}
                                   onChange={e => updateDeptHead(dept, e.target.value)}
                                   className="w-full text-xs bg-transparent outline-none border-b"
                                   style={{ color: 'var(--cb-text)', borderColor: 'var(--cb-border)' }}
                                   placeholder="Dept. Head" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {!previewMode && unassignedDepts.length > 0 && (
            <button onClick={addSlot}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-colors"
                    style={{ border: '1px dashed var(--cb-border)', color: 'var(--cb-text-dim)', background: 'transparent' }}
                    data-testid="add-crew-slot-btn">
              <Plus className="h-4 w-4" />
              Add Another Call Time
              <span className="text-xs opacity-60">({unassignedDepts.length} dept{unassignedDepts.length > 1 ? 's' : ''} remaining)</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── 8. Locations ──────────────────────────────────────────────────────────────
function LocationsSection({ sheet, update, previewMode }) {
  const locs = sheet.locations || [];
  const sceneGroups = buildLocationSceneGroups(sheet.scenes || []);

  function addLoc() {
    update('locations', [...locs, {
      id: crypto.randomUUID(), name: '', address: '', gps_link: '',
      contact_name: '', contact_phone: '', parking_info: '',
      unit_base: '', travel_time: '', notes: '', department_notes: {}
    }]);
  }
  function removeLoc(id) { update('locations', locs.filter(l => l.id !== id)); }
  function updateLoc(id, f, v) { update('locations', locs.map(l => l.id === id ? {...l, [f]: v} : l)); }

  return (
    <div>
      <SectionHeader id="locations" icon={MapPin} title={`Locations (${locs.length})`}
                     action={
                       !previewMode && (
                         <Button size="sm" variant="ghost" onClick={addLoc}
                                 className="gap-1.5 text-xs" style={{ color: 'var(--cb-accent)' }} data-testid="add-location-management-button">
                           <Plus className="h-3.5 w-3.5" /> Add Location
                         </Button>
                       )
                     } />

      {locs.length === 0 ? (
        <div className="text-center py-10 rounded-xl" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)', color: 'var(--cb-text-dim)' }}>
          No locations. Sync from schedule or add manually.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {sceneGroups.map((group, index) => (
              <div key={`${group.key}-${index}`} className="rounded-xl p-3" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }} data-testid={`location-management-group-${index}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--cb-text-dim)' }}>Scene Group</div>
                <div className="text-sm font-bold" style={{ color: 'var(--cb-text)' }}>{group.name}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--cb-text-sec)' }}>{group.sceneCount} scene(s) · {group.totalPages.toFixed(2)} pages</div>
              </div>
            ))}
          </div>
          {locs.map(loc => (
            <div key={loc.id} className="rounded-xl overflow-hidden group"
                 style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
              <div className="px-5 py-3 flex items-center justify-between"
                   style={{ background: 'var(--cb-bg)', borderBottom: '1px solid var(--cb-border)' }}>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" style={{ color: '#F59E0B' }} />
                  <input value={loc.name} readOnly={previewMode} onChange={e => updateLoc(loc.id, 'name', e.target.value)}
                         className="text-base font-bold bg-transparent outline-none"
                         style={{ color: 'var(--cb-text)' }} placeholder="Location Name" />
                </div>
                {!previewMode && <button onClick={() => removeLoc(loc.id)} className="opacity-0 group-hover:opacity-100" style={{ color: '#EF4444' }}>
                  <Trash2 className="h-4 w-4" />
                </button>}
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  {[
                    { label: 'Address',       field: 'address' },
                    { label: 'GPS / Map Link', field: 'gps_link' },
                    { label: 'Contact Name',  field: 'contact_name' },
                    { label: 'Contact Phone', field: 'contact_phone' },
                    { label: 'Parking',       field: 'parking_info' },
                    { label: 'Travel Time',   field: 'travel_time' },
                  ].map(({ label, field }) => (
                    <div key={field}>
                      <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--cb-text-dim)' }}>{label}</div>
                      <input value={loc[field] || ''} readOnly={previewMode} onChange={e => updateLoc(loc.id, field, e.target.value)}
                             className="w-full text-sm bg-transparent outline-none border-b"
                             style={{ color: 'var(--cb-text)', borderColor: 'var(--cb-border)' }} placeholder={label} />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: 'var(--cb-text-dim)' }}>Location Notes</div>
                  <textarea value={loc.notes || ''} readOnly={previewMode} onChange={e => updateLoc(loc.id, 'notes', e.target.value)}
                            className="w-full text-sm bg-transparent outline-none resize-none rounded px-2 py-1.5"
                            style={{ color: 'var(--cb-text)', border: '1px solid var(--cb-border)', minHeight: '60px' }}
                            placeholder="Access instructions, catering, notes…" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 9. Department Notes ───────────────────────────────────────────────────────
function DeptNotesSection({ sheet, updateNested, previewMode }) {
  const notes = sheet.department_notes || {};
  const scenes = sheet.scenes || [];

  // Build scene-wise props reference from scenes in the call sheet
  const sceneProps = scenes
    .filter(s => s.props && s.props.length > 0)
    .map(s => ({ sceneNum: s.scene_number || '?', props: s.props }));

  return (
    <div>
      <SectionHeader id="dept-notes" icon={Edit3} title="Department Notes" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
        {DEPT_NOTES_LIST.map(({ key, label, color }) => (
          <div key={key} className="rounded-xl overflow-hidden"
               style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
            <div className="h-0.5" style={{ background: color }} />
            <div className="px-3 pt-3 pb-1">
              <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color }}>
                {label}
              </div>
              <textarea value={notes[key] || ''}
                        readOnly={previewMode}
                        onChange={e => updateNested('department_notes', key, e.target.value)}
                        className="w-full text-xs bg-transparent outline-none resize-none"
                        style={{ color: 'var(--cb-text)', minHeight: '72px' }}
                        placeholder={previewMode ? '' : `Notes for ${label}…`}
                        data-testid={`dept-note-${key}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Scene Action Props Reference */}
      {sceneProps.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--cb-border)' }}>
          <div className="px-4 py-2.5 flex items-center gap-2"
               style={{ background: 'var(--cb-bg)', borderBottom: '1px solid var(--cb-border)' }}>
            <Sparkles className="h-3.5 w-3.5" style={{ color: 'var(--cb-accent)' }} />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--cb-text-dim)' }}>
              Scene Action Props Reference
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--cb-accent-dim)', color: 'var(--cb-accent)' }}>
              auto
            </span>
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--cb-border)' }}>
            {sceneProps.map(({ sceneNum, props }) => (
              <div key={sceneNum} className="flex items-start gap-3 px-4 py-2.5"
                   style={{ background: 'var(--cb-surface)' }}>
                <span className="text-xs font-black font-mono shrink-0 mt-0.5 px-2 py-0.5 rounded"
                      style={{ background: 'var(--cb-accent-dim)', color: 'var(--cb-accent)', minWidth: '40px', textAlign: 'center' }}>
                  #{sceneNum}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {props.map((p, pi) => (
                    <span key={pi} className="text-[11px] px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(99,102,241,0.12)', color: '#818CF8', border: '1px solid rgba(99,102,241,0.2)' }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── 10. Attachments ───────────────────────────────────────────────────────────
function AttachmentsSection({ sheet, update }) {
  const TYPE_LABELS = {
    shot_list: 'Shot List', storyboard: 'Storyboard', map: 'Map',
    pdf: 'PDF', image: 'Image', floor_plan: 'Floor Plan', moodboard: 'Moodboard',
  };
  const attachments = sheet.attachments || [];
  function removeAtt(id) { update('attachments', attachments.filter(a => a.id !== id)); }

  return (
    <div>
      <SectionHeader id="attachments" icon={Image} title="Attachments" />
      <div className="rounded-xl p-4" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
        <div className="border-2 border-dashed rounded-xl p-8 text-center mb-4" style={{ borderColor: 'var(--cb-border)' }}>
          <Image className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--cb-text-dim)' }} />
          <div className="text-sm mb-1" style={{ color: 'var(--cb-text-sec)' }}>Shot lists, storyboards, maps, floor plans, PDFs</div>
          <div className="text-xs" style={{ color: 'var(--cb-text-dim)' }}>File upload coming soon</div>
        </div>
        {attachments.map(att => (
          <div key={att.id} className="flex items-center gap-3 px-3 py-2 rounded-lg group"
               style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)' }}>
            <span className="text-sm font-medium" style={{ color: 'var(--cb-accent)' }}>{TYPE_LABELS[att.file_type] || 'File'}</span>
            <span className="flex-1 text-sm" style={{ color: 'var(--cb-text)' }}>{att.filename}</span>
            <span className="text-xs" style={{ color: 'var(--cb-text-dim)' }}>{att.uploaded_at?.slice(0, 10)}</span>
            <button onClick={() => removeAtt(att.id)} className="opacity-0 group-hover:opacity-100" style={{ color: '#EF4444' }}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  Scene Picker Modal
// ═══════════════════════════════════════════════════════════════════════════

function ScenePickerModal({ projectId, token, existingSceneIds, onAdd, onClose }) {
  const [scenes, setScenes]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(new Set());

  useEffect(() => {
    fetch(`${API}/api/projects/${projectId}/call-sheets-scenes`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setScenes(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [projectId, token]);

  const filtered = scenes.filter(s => {
    if (existingSceneIds.has(s.id)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(s.scene_number ?? '').toLowerCase().includes(q) ||
      s.location.toLowerCase().includes(q) ||
      (s.synopsis || '').toLowerCase().includes(q)
    );
  });

  function toggleSelect(id) {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  }

  function selectAll() {
    setSelected(new Set(filtered.map(s => s.id)));
  }

  function handleAdd() {
    const picked = scenes.filter(s => selected.has(s.id));
    const csScenes = picked.map((s, i) => ({
      id: crypto.randomUUID(),
      scene_id: s.id,
      order: i,
      scene_number: s.scene_number,
      int_ext: s.int_ext,
      day_night: s.day_night,
      pages: (s.page_count || 0) / 8,
      location: s.location,
      description: s.synopsis || '',
      cast: s.characters || [],
      extras: 0,
      props: s.action_props || [],
      vehicles: s.vehicles || [],
      vfx: s.vfx || [],
      special_effects: s.special_effects || [],
      stunts: s.stunts || [],
      notes: '',
      estimated_duration: '',
    }));
    onAdd(csScenes);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
         onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[80vh] rounded-2xl flex flex-col"
           style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4"
             style={{ borderBottom: '1px solid var(--cb-border)' }}>
          <div>
            <h3 className="font-bold text-base" style={{ color: 'var(--cb-text)' }}>Add Scenes from Breakdown</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--cb-text-dim)' }}>
              Cast, props & vehicles are auto-added from breakdown
            </p>
          </div>
          <button onClick={onClose} style={{ color: 'var(--cb-text-sec)' }}><X className="h-5 w-5" /></button>
        </div>

        {/* Search */}
        <div className="px-5 py-3" style={{ borderBottom: '1px solid var(--cb-border)' }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--cb-text-dim)' }} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by scene #, location, synopsis…"
              className="w-full pl-10 pr-4 py-2 rounded-lg text-sm outline-none"
              style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text)' }}
              data-testid="scene-picker-search"
              autoFocus
            />
          </div>
        </div>

        {/* Scene list */}
        <div className="flex-1 overflow-y-auto px-5 py-2">
          {loading ? (
            <div className="text-center py-10 text-sm" style={{ color: 'var(--cb-text-dim)' }}>Loading scenes…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-sm" style={{ color: 'var(--cb-text-dim)' }}>
              {search ? 'No matching scenes' : 'All scenes already added'}
            </div>
          ) : (
            <div className="space-y-1">
              {/* Select all */}
              <button onClick={selectAll} className="w-full text-left px-3 py-1.5 text-xs rounded"
                      style={{ color: 'var(--cb-accent)' }}>
                Select all ({filtered.length})
              </button>
              {filtered.map(s => {
                const isSelected = selected.has(s.id);
                const special = [
                  ...(s.vehicles || []),
                  ...(s.vfx || []),
                  ...(s.stunts || []),
                  ...(s.action_props || []).slice(0, 3),
                ].filter(Boolean).join(', ');

                return (
                  <div key={s.id}
                       onClick={() => toggleSelect(s.id)}
                       className="flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors"
                       style={{
                         background: isSelected ? 'var(--cb-accent-dim)' : 'var(--cb-bg)',
                         border: `1px solid ${isSelected ? 'var(--cb-accent)' : 'var(--cb-border)'}`,
                       }}
                       data-testid={`scene-picker-item-${s.id}`}>
                    {/* Checkbox */}
                    <div className="h-4 w-4 rounded shrink-0 mt-0.5 flex items-center justify-center"
                         style={{
                           background: isSelected ? 'var(--cb-accent)' : 'transparent',
                           border: `2px solid ${isSelected ? 'var(--cb-accent)' : 'var(--cb-border)'}`,
                         }}>
                      {isSelected && <Check className="h-3 w-3 text-black" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Scene meta row */}
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-black font-mono" style={{ color: 'var(--cb-accent)' }}>
                          {formatSceneNumber(s.scene_number, { prefix: '#' })}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                              style={{ background: 'var(--cb-surface)', color: 'var(--cb-text-sec)' }}>
                          {s.int_ext}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                              style={{ background: 'var(--cb-surface)', color: 'var(--cb-text-sec)' }}>
                          {s.day_night}
                        </span>
                        {s.location && (
                          <span className="text-xs font-medium truncate" style={{ color: 'var(--cb-text)' }}>
                            {s.location}
                          </span>
                        )}
                        {s.page_count > 0 && (
                          <span className="text-[10px] ml-auto shrink-0" style={{ color: 'var(--cb-text-dim)' }}>
                            {(s.page_count / 8).toFixed(2)}p
                          </span>
                        )}
                      </div>

                      {/* Synopsis */}
                      {s.synopsis && (
                        <p className="text-xs leading-snug mb-1.5 line-clamp-2" style={{ color: 'var(--cb-text-sec)' }}>
                          {s.synopsis}
                        </p>
                      )}

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1">
                        {(s.characters || []).slice(0, 4).map(c => (
                          <span key={c} className="text-[10px] px-1.5 py-0.5 rounded"
                                style={{ background: 'rgba(236,72,153,0.15)', color: '#EC4899' }}>
                            {c}
                          </span>
                        ))}
                        {(s.vehicles || []).slice(0, 2).map(v => (
                          <span key={v} className="text-[10px] px-1.5 py-0.5 rounded"
                                style={{ background: 'rgba(100,116,139,0.15)', color: '#94A3B8' }}>
                            {v}
                          </span>
                        ))}
                        {(s.vfx || []).slice(0, 2).map(v => (
                          <span key={v} className="text-[10px] px-1.5 py-0.5 rounded"
                                style={{ background: 'rgba(167,139,250,0.15)', color: '#A78BFA' }}>
                            {v}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4"
             style={{ borderTop: '1px solid var(--cb-border)' }}>
          <div className="text-sm" style={{ color: 'var(--cb-text-sec)' }}>
            {selected.size > 0 ? (
              <span><span className="font-bold" style={{ color: 'var(--cb-accent)' }}>{selected.size}</span> scene(s) selected</span>
            ) : (
              'Click scenes to select'
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}
                    style={{ borderColor: 'var(--cb-border)', color: 'var(--cb-text-sec)' }}>
              Cancel
            </Button>
            <Button size="sm" disabled={selected.size === 0} onClick={handleAdd}
                    style={{ background: 'var(--cb-accent)', color: '#000', fontWeight: 700 }}
                    data-testid="confirm-add-scenes">
              Add {selected.size > 0 ? `${selected.size} Scene${selected.size > 1 ? 's' : ''}` : 'Scenes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  Share Modal
// ═══════════════════════════════════════════════════════════════════════════

function ShareModal({ sheet, projectId, sheetId, token, onClose }) {
  const shareUrl    = `${window.location.origin}/projects/${projectId}/callsheet/${sheetId}`;
  const prodName    = sheet.production_name || sheet.title || 'Call Sheet';
  const shareTitle  = `${prodName} — Day ${sheet.shooting_day} Call Sheet (${sheet.date})`;
  const qrUrl       = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(shareUrl)}&bgcolor=ffffff&color=0d0f14`;

  const [copied, setCopied] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [waStep, setWaStep] = useState('idle'); // 'idle' | 'downloading' | 'ready'
  const [showPdfLanguageDialog, setShowPdfLanguageDialog] = useState(false);
  const [pendingPdfAction, setPendingPdfAction] = useState('download');

  async function handleDownloadPdf(synopsisLang = 'english') {
    setDownloadingPdf(true);
    try {
      await downloadCallSheetPdf({
        apiBase: API,
        projectId,
        sheetId,
        token,
        synopsisLang,
        filename: getCallSheetPdfFilename(sheet),
      });
      toast.success('PDF downloaded!');
    } catch (error) { toast.error(error.message || 'PDF download failed'); }
    finally { setDownloadingPdf(false); }
  }

  async function handleWhatsApp(synopsisLang = 'english') {
    setWaStep('downloading');
    try {
      const blob = await fetchCallSheetPdfBlob({
        apiBase: API,
        projectId,
        sheetId,
        token,
        synopsisLang,
      });
      downloadBlobFile(blob, getCallSheetPdfFilename(sheet));
    } catch (error) {
      toast.error(error.message || 'PDF download failed');
      setWaStep('idle');
      return;
    }
    setWaStep('ready');
    const crewCall = sheet.crew_call ? `\nCrew Call: ${sheet.crew_call}` : '';
    const sceneCount = (sheet.scenes || []).length;
    const msg = encodeURIComponent(
      `*${shareTitle}*${crewCall}\nScenes: ${sceneCount}\n\nView Call Sheet: ${shareUrl}\n\n_PDF attached separately_`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
    setTimeout(() => setWaStep('idle'), 3000);
  }

  async function confirmPdfAction(synopsisLang) {
    setShowPdfLanguageDialog(false);
    if (pendingPdfAction === 'whatsapp') {
      await handleWhatsApp(synopsisLang);
      return;
    }
    await handleDownloadPdf(synopsisLang);
  }

  function copyLink() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
         onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl"
           style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4"
             style={{ borderBottom: '1px solid var(--cb-border)' }}>
          <div>
            <h3 className="font-bold" style={{ color: 'var(--cb-text)' }}>Share Call Sheet</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--cb-text-dim)' }}>Day {sheet.shooting_day} · {sheet.date}</p>
          </div>
          <button onClick={onClose} style={{ color: 'var(--cb-text-sec)' }}><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Share link */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cb-text-dim)' }}>
              Share Link
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 rounded-lg text-xs font-mono truncate"
                   style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text-sec)' }}>
                {shareUrl}
              </div>
              <button onClick={copyLink}
                      className="px-3 py-2 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5"
                      style={{
                        background: copied ? 'rgba(34,197,94,0.15)' : 'var(--cb-accent)',
                        color: copied ? '#22C55E' : '#000',
                      }}
                      data-testid="copy-link-btn">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* WhatsApp — downloads PDF then opens WA */}
          <button onClick={() => {
                    setPendingPdfAction('whatsapp');
                    setShowPdfLanguageDialog(true);
                  }} disabled={waStep === 'downloading'}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl w-full transition-colors text-left"
                  style={{ background: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.3)', color: '#25D366' }}
                  data-testid="whatsapp-btn">
            <MessageCircle className="h-5 w-5 shrink-0" />
            <div>
              <div className="text-sm font-bold">
                {waStep === 'downloading' ? 'Downloading PDF…' : waStep === 'ready' ? 'WhatsApp Opening…' : 'Send via WhatsApp'}
              </div>
              <div className="text-xs opacity-70">Downloads PDF + sends link with details</div>
            </div>
          </button>

          {/* QR Code */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cb-text-dim)' }}>
              QR Code
            </div>
            <div className="flex items-center justify-center p-4 rounded-xl"
                 style={{ background: 'white', border: '1px solid var(--cb-border)' }}>
              <img src={qrUrl} alt="QR Code" className="w-[160px] h-[160px]"
                   onError={e => { e.target.style.display = 'none'; }}
                   data-testid="share-qr-code" />
            </div>
            <p className="text-xs text-center mt-2" style={{ color: 'var(--cb-text-dim)' }}>
              Scan to open call sheet
            </p>
          </div>

          {/* PDF */}
          <button
            onClick={() => {
              setPendingPdfAction('download');
              setShowPdfLanguageDialog(true);
            }}
            disabled={downloadingPdf}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors"
            style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text)' }}>
            <Download className="h-4 w-4" style={{ color: 'var(--cb-accent)' }} />
            <div className="text-left">
              <div className="text-sm font-medium">{downloadingPdf ? 'Generating…' : 'Download PDF'}</div>
              <div className="text-xs" style={{ color: 'var(--cb-text-dim)' }}>Studio-grade print-ready format</div>
            </div>
          </button>
        </div>

        <PdfLanguageDialog
          open={showPdfLanguageDialog}
          onClose={() => setShowPdfLanguageDialog(false)}
          onConfirm={confirmPdfAction}
          loading={downloadingPdf || waStep === 'downloading'}
          title={pendingPdfAction === 'whatsapp' ? 'Choose synopsis language before sharing' : 'Choose call sheet PDF synopsis language'}
        />
      </div>
    </div>
  );
}
